import { inject, Injectable, OnDestroy } from '@angular/core';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  AuthenticationResult,
  EventError,
  EventMessage,
  EventPayload,
  EventType,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { EnvironmentBase } from '@environments/environment.base';
import { JsonWebToken, LoadingState, localStorageKeys } from '@lib-ui/lib/models';
import { RoleService } from '@lib-ui/lib/services/role.service';
import { UserService } from '@lib-ui/lib/services/user.service';
import { NEVER, Observable, Subject } from 'rxjs';
import { ProtectedResource } from '@lib-ui/lib/models/protected-resource';
import { fromPromise } from 'rxjs/internal-compatibility';
import { toObservable } from '@angular/core/rxjs-interop';
import { decodeToken } from '@lib-ui/lib/functions/jwt-parser';

@Injectable()
export class AuthenticationService implements OnDestroy {
  private readonly destroying$ = new Subject<void>();
  private readonly userService = inject(UserService);
  private readonly loginSuccess$ = toObservable(this.userService.state.status).pipe(
    filter(status => status == LoadingState.Success),
  );

  constructor(
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private rolesService: RoleService,
    private router: Router,
  ) {
  }

  public init(protectedResources: ProtectedResource, environment: Partial<EnvironmentBase>): void {
    this.relog(protectedResources).subscribe({
      next: () => console.log('relog success'),
      error: err => console.log('relog failed', err),
    });

    this.loginSuccess().subscribe();

    this.logoutEnd().subscribe(() => {
      localStorage.removeItem(localStorageKeys.USER_KEY);
      localStorage.removeItem(localStorageKeys.ACCESS_TOKEN_KEY);
      this.userService.logout();
    });

    this.acquireTokenFailure().subscribe((result: EventMessage) => {
      console.log('TOKEN FAILURE:', result);
      this.logout();
    });

    this.loginFailure().subscribe((hasForgotPassword: boolean) => {
      if (hasForgotPassword) {
        return this.handleForgotPassword(environment.AadPasswordResetUrl ?? '', protectedResources);
      } else {
        return this.msalAuthService.loginRedirect();
      }
    });
  }

  public logout(): Observable<void> {
    this.rolesService.clearCachedRoles();
    return this.msalAuthService.logoutRedirect();
  }

  public parseToken(token: string): JsonWebToken {
    const rawToken = decodeToken(token);
    //console.log('parsing raw tokens:', rawToken); TODO: Nach Abschluss B2C entfernen.

    const jwt: JsonWebToken = {
      accessToken: token,
      id: rawToken.sub,
      expires: rawToken.exp * 100,
      name: rawToken.name,
      roles: [],
    };

    // In some scenarios the email can be undefined and only the emails array is filled.
    if (rawToken.email) {
      jwt.email = rawToken.email;
    } else if (rawToken.emails && rawToken.emails[0]) {
      jwt.email = rawToken.emails[0];
    }
    return jwt;
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.unsubscribe();
  }

  private loginSuccess() {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter(
        (msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
      ),
      takeUntil(this.destroying$),
      switchMap((authenticationResult: EventMessage) =>
        this.processAuthenticationResult(authenticationResult.payload),
      ),
    );
  }

  private logoutEnd() {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_END),
    );
  }

  private acquireTokenFailure() {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
      takeUntil(this.destroying$),
    );
  }

  private loginFailure() {
    return this.msalBroadcastService.msalSubject$.pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
      takeUntil(this.destroying$),
      tap((result: EventMessage) => console.log('LOGIN_FAILURE:', result)),
      map((result: EventMessage) => this.hasForgotPasswordErrorCode(result.error)),
    );
  }

  private handleForgotPassword(passwortResetUrl: string, protectedResources: ProtectedResource) {
    const resetPasswordFlowRequest: RedirectRequest = {
      authority: passwortResetUrl,
      scopes: [...protectedResources.default.scopes, 'openid'],
    };
    return this.msalAuthService.loginRedirect(resetPasswordFlowRequest);
  }

  private hasForgotPasswordErrorCode(error: EventError) {
    const ForgotPasswordCode = 'AADB2C90118';
    return !!(error && error.message.indexOf(ForgotPasswordCode) > -1);
  }

  private navigateHome() {
    console.log('navigating to /jobs');
    this.router.navigate(['/jobs']);
  }

  private relog(protectedResources: ProtectedResource) {
    const account = this.msalAuthService.instance.getAllAccounts()[0];
    if (!account) {
      return NEVER;
    }
    const request: SilentRequest = {
      scopes: [...protectedResources.default.scopes, 'openid'],
      account: account,
    };
    return fromPromise(this.msalAuthService.instance.initialize()).pipe(
      switchMap(() => this.msalAuthService.acquireTokenSilent(request)),
    );
  }

  private processAuthenticationResult(eventPayload: EventPayload) {
    const authenticationResult = eventPayload as AuthenticationResult;
    if (!authenticationResult || !authenticationResult.accessToken) {
      return this.logout();
    }
    const accessToken = authenticationResult.accessToken;
    localStorage.setItem(localStorageKeys.ACCESS_TOKEN_KEY, accessToken);
    const parsedToken = this.parseToken(accessToken);
    if (!parsedToken.email) {
      return this.logout();
    }
    this.userService.getUserByExternalId(parsedToken.id);
    return this.loginSuccess$;
  }
}
